import { encode, decode } from 'blurhash';

const MAX_ENCODE_DIMENSION = 64;
const DEFAULT_COMPONENT_X = 4;
const DEFAULT_COMPONENT_Y = 3;

/** Encode an image file into a compact BlurHash string (images only). */
export async function encodeBlurHashFromFile(file: File): Promise<string | null> {
  if (!file.type.startsWith('image/')) {
    return null;
  }

  const bitmap = await loadImageBitmap(file);
  const { width, height, imageData } = drawToImageData(bitmap);
  if ('close' in bitmap && typeof bitmap.close === 'function') {
    bitmap.close();
  }

  return encode(imageData.data, width, height, DEFAULT_COMPONENT_X, DEFAULT_COMPONENT_Y);
}

/** Decode a BlurHash string to a CSS-ready data URL. */
export function blurHashToDataUrl(
  hash: string,
  width = 32,
  height = 18
): string {
  if (!hash?.trim()) {
    return '';
  }

  try {
    const pixels = decode(hash, width, height);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return '';
    }

    const imageData = ctx.createImageData(width, height);
    imageData.data.set(pixels);
    ctx.putImageData(imageData, 0, 0);
    return canvas.toDataURL('image/png');
  } catch {
    return '';
  }
}

function loadImageBitmap(file: File): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === 'function') {
    return createImageBitmap(file);
  }

  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image for BlurHash'));
    };
    img.src = url;
  });
}

function drawToImageData(source: ImageBitmap | HTMLImageElement): {
  width: number;
  height: number;
  imageData: ImageData;
} {
  const sourceWidth = 'width' in source ? source.width : (source as HTMLImageElement).naturalWidth;
  const sourceHeight = 'height' in source ? source.height : (source as HTMLImageElement).naturalHeight;
  const scale = Math.min(1, MAX_ENCODE_DIMENSION / Math.max(sourceWidth, sourceHeight));
  const width = Math.max(1, Math.round(sourceWidth * scale));
  const height = Math.max(1, Math.round(sourceHeight * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) {
    throw new Error('Canvas unavailable for BlurHash');
  }

  ctx.drawImage(source as CanvasImageSource, 0, 0, width, height);
  return { width, height, imageData: ctx.getImageData(0, 0, width, height) };
}
